import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { List, MessageCircle, Bookmark, Play, Layers, Filter, Loader2, MapPin } from "lucide-react";
import { L, tr } from "@/lib/i18n";
import { perfRecord, logFallback, FALLBACK_REASONS } from "@/lib/perfLog";
import { useScenePrewarm } from "@/lib/hooks/useScenePrewarm";
import useDocumentLocale from "@/hooks/useDocumentLocale";
import { useAutoplayUnlock } from "@/lib/hooks/useAutoplayUnlock";
import { useShortlist, toggleShortlist as toggleShortlistGlobal } from "@/lib/shortlist";
import { resolveUnitLocations } from "@/lib/unitLocate";
import { captureVideoFrame, releaseCanvasFrame } from "@/lib/videoFrame";
import { toast } from "sonner";
import ViewerDebugPanel, { shouldShowDebugPanel } from "./components/ViewerDebugPanel";
import ScrubOverlay from "./components/ScrubOverlay";
import FloorStackView from "./components/FloorStackView";
import UnitFinderDrawer from "./components/UnitFinderDrawer";
import UnitsList from "./components/UnitsList";
import UnitDetail from "./components/UnitDetail";
import ShortlistPanel from "./components/ShortlistPanel";
import ComparePanel from "./components/ComparePanel";
import LocationMap from "./components/LocationMap";
import PresentationMode from "./components/PresentationMode";
import PresentationAudio from "./components/PresentationAudio";
import ShowroomButton from "./components/ShowroomButton";
import AmbientAudio from "./components/AmbientAudio";
import SceneCanvas from "./components/SceneCanvas";
import SceneNavigation from "./components/SceneNavigation";
import LetterboxBackdrop from "./components/LetterboxBackdrop";
import { HANDOFF_FADE_MS, MEDIA_VIGNETTE } from "./lib/mediaPresentation";
import LeadFormPanel from "./components/LeadFormPanel";
import { LanguageSwitcher, Loading, Unavailable, ZoneInfoPanel } from "./components/ViewerStates";
import { waLink } from "./components/statusUtil";
import {
  OPPOSITE, AXIS_OF, DRAG_INTENT_PX, AUTOPLAY_SNIFF_MS,
  buildEffectiveTransitions,
} from "./lib/transitionHelpers";

// Direction utilities for bidirectional reverse transitions ------------------
// The pure math (OPPOSITE, AXIS_OF, buildEffectiveTransitions, computeFit) lives
// in `./lib/transitionHelpers` — imported above. Keeping this file focused on
// state/view wiring.

export default function PublicViewer({ embed }) {
  const { slug, etapCode } = useParams();
  const [searchParams] = useSearchParams();
  const urlUnitCode = searchParams.get("unit");        // ?unit=A-203 deep link
  const urlShortlist = searchParams.get("shortlist");  // ?shortlist=A-101,A-203
  // When the URL carries an etapCode (`/p/:slug/etap/:code`), we jump
  // straight into the Units list scoped to that Etap. Used by sales teams
  // sharing per-phase launch links on socials.
  const initialMode = etapCode ? "list" : "tour";
  const [structure, setStructure] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(initialMode);
  // `compareCodes` is the set passed into the ComparePanel when opened
  // from the ShortlistPanel — kept here at the viewer level so transient
  // shortlist edits during compare don't crash the panel.
  const [compareCodes, setCompareCodes] = useState([]);
  const [finderState, setFinderState] = useState({});
  const [listState, setListState] = useState({});
  const plansByUnitRef = useRef({});
  const shortlistCodes = useShortlist(slug);
  // Phase B Feature 7 — buyer journey context for lead capture. Refs (not
  // state) because we never need to re-render on these — they only matter
  // at lead-submission time.
  const journeyRef = useRef({
    plan_opened: false, gallery_opened: false, video_opened: false,
    pdf_downloaded: false, whatsapp_clicked: false,
    selected_payment_plan_id: null, compared_codes: [],
  });
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [activeSceneId, setActiveSceneId] = useState(null);
  const activeSceneIdRef = useRef(activeSceneId);
  activeSceneIdRef.current = activeSceneId;
  const [failedSceneId, setFailedSceneId] = useState(null);
  const [readySceneId, setReadySceneId] = useState(null);
  const [previousSceneId, setPreviousSceneId] = useState(null);
  const [transitionState, setTransitionState] = useState(null);
  const transitionStateRef = useRef(transitionState);
  transitionStateRef.current = transitionState;
  const transitionFrame = transitionState?.frozenFrame;
  // A rapid handoff may reuse this same frame. Release only when its owner
  // replaces it, finishes the fade, or unmounts the viewer.
  useEffect(() => () => releaseCanvasFrame(transitionFrame), [transitionFrame]);
  // Fade only onto confirmed displayable pixels: decoded image, failed-video poster,
  // captured first video frame or playback. A timer cannot prove readiness.
  const beginSceneFade = useCallback((sceneId) => {
    setFailedSceneId(current => current === sceneId ? null : current);
    setTransitionState(current => current?.toId === sceneId && !current.fading
      ? { ...current, fading: true } : current);
  }, []);
  const handleMediaDisplayFailure = useCallback((sceneId) => {
    if (activeSceneIdRef.current !== sceneId) return;
    const handoff = transitionStateRef.current;
    if (handoff?.toId === sceneId && !handoff.recovering && handoff.fromId !== sceneId) {
      // Return to the known source rather than revealing an empty target.
      // Keep the existing frame until that source has usable pixels again.
      setActiveSceneId(handoff.fromId);
      setTransitionState({ ...handoff, toId: handoff.fromId, fading: false, recovering: true });
      return;
    }
    setFailedSceneId(sceneId);
    setTransitionState(null);
  }, []);
  useEffect(() => {
    if (!transitionState?.recovering || transitionState.fading) return undefined;
    const timer = setTimeout(() => {
      if (transitionStateRef.current !== transitionState) return;
      // A failed restore must not leave a different camera frame under the
      // source label forever. Show a clear state and keep navigation usable.
      setFailedSceneId(transitionState.toId);
      setTransitionState(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [transitionState]);
  useEffect(() => {
    if (!transitionState?.fading) return undefined;
    const timer = setTimeout(() => {
      setTransitionState(current => {
        if (current !== transitionState) return current;
        return null;
      });
    }, HANDOFF_FADE_MS);
    return () => clearTimeout(timer);
  }, [transitionState]);
  const [pendingScrub, setPendingScrub] = useState(null); // {transition, fromId, toId}
  const pendingScrubRef = useRef(null);
  pendingScrubRef.current = pendingScrub;
  // Warm only the next image while its transition is playing. No extra video
  // decoder is created, and changing/cancelling the transition drops the owner.
  useEffect(() => {
    if (!pendingScrub?.toId || !structure) return undefined;
    const destination = structure.scenes.find(scene => scene.id === pendingScrub.toId);
    const src = destination?.type === "static_image" ? destination.media_url : destination?.poster_url;
    if (!src) return undefined;
    const image = new Image();
    image.decoding = "async";
    image.src = src;
    // Large plans/scenes should warm their bytes without forcing a second
    // full-resolution decoded bitmap while the transition is still playing.
    const pixels = (destination.media_width || 0) * (destination.media_height || 0);
    if (pixels > 0 && pixels <= 1280 * 720 && typeof image.decode === "function") image.decode().catch(() => {});
    return () => { image.removeAttribute("src"); };
  }, [pendingScrub?.toId, structure]);
  const [lang, setLang] = useState(null);
  const sessionIdRef = useRef(Math.random().toString(36).slice(2));
  const [videoStatus, setVideoStatus] = useState(null);
  const debugVisible = shouldShowDebugPanel();
  // V1.1: info popover for zones with click_action='show_info'.
  const [zoneInfo, setZoneInfo] = useState(null); // { title, description, image_url }
  // V1.1: live-drag scrub state owned at PublicViewer level. SceneCanvas
  // tracks the gesture (since the finger is captured there) and pushes
  // progress into this state; ScrubOverlay renders the frame at that
  // progress without owning its own pointer handlers in this path.
  const [liveScrubProgress, setLiveScrubProgress] = useState(null);
  const [scrubReleaseTarget, setScrubReleaseTarget] = useState(null);
  const scrubReleaseTargetRef = useRef(null);
  scrubReleaseTargetRef.current = scrubReleaseTarget;
  // Phase C Feature 10 — Presentation mode (guided sequence).
  const [presentationIndex, setPresentationIndex] = useState(null); // null = not running
  const [locationOpen, setLocationOpen] = useState(false);
  // Phase C — initial UnitDetail tab when opened via a presentation step
  // (e.g. unit_plan/unit_gallery). Cleared after Unit panel unmounts.
  const [unitInitialTab, setUnitInitialTab] = useState(null);
  // Phase 5B — Remember how the user opened the current Unit Detail panel
  // so the "Back to list" action returns them to the correct overlay
  // (FloorStackView, UnitsList, Shortlist, …) instead of always UnitsList.
  const [unitOpenSource, setUnitOpenSource] = useState("list");

  const trackEvent = useCallback((event_type, metadata = {}) => {
    // Mirror critical events into the lead-journey ref so the lead form
    // attaches them on submit (Phase B Feature 7). 'share_whatsapp' is
    // the share-menu path; 'whatsapp_click' is the bottom CTA. Both
    // mean "buyer used WhatsApp" for the lead-journey signal.
    if (event_type === "whatsapp_click" || event_type === "share_whatsapp") journeyRef.current.whatsapp_clicked = true;
    if (event_type === "pdf_download") journeyRef.current.pdf_downloaded = true;
    if (event_type === "compare_open" && metadata?.codes) {
      journeyRef.current.compared_codes = metadata.codes;
    }
    api.post("/public/events", {
      project_slug: slug, event_type,
      session_id: sessionIdRef.current,
      scene_id: activeSceneId, ...metadata,
    }).catch(() => {});
  }, [slug, activeSceneId]);

  /**
   * Server-rendered PDFs for single unit / shortlist / compare.
   * Streams the file via fetch + Blob so we keep the same axios baseURL
   * configuration the rest of the viewer uses.
   */
  const downloadBulkPdf = useCallback(async (codes, kind) => {
    try {
      const r = await api.post(
        `/public/projects/${slug}/units/pdf`,
        { codes, mode: kind, lang: lang || "en", plan_ids: Object.fromEntries(codes.map(code => [code, plansByUnitRef.current[code]]).filter(([, id]) => id)) },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-${kind}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      trackEvent("pdf_download", { kind, count: codes.length });
      journeyRef.current.pdf_downloaded = true;
    } catch (_e) {
      toast.error(L(lang, "pdf_failed"));
    }
  }, [slug, lang, trackEvent]);

  // Load data + initialize language
  useEffect(() => {
    // Safari (Private Browsing / "Block All Cookies") throws on localStorage
    // access. These wrappers keep storage failures from ever blanking the
    // viewer with a bogus "Project not found".
    const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
    const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* blocked storage */ } };
    (async () => {
      const tStart = performance.now();
      let s, i;
      // ONLY a genuine fetch failure (or 410) should surface an error state.
      try {
        [s, i] = await Promise.all([
          api.get(`/public/projects/${slug}/structure`),
          api.get(`/public/projects/${slug}/inventory`),
        ]);
      } catch (e) {
        if (e?.response?.status === 410) setError("unavailable");
        else setError("not_found");
        return;
      }
      setStructure(s.data);
      setInventory(i.data);
      const def = s.data.scenes.find(sc => sc.is_default) || s.data.scenes[0];
      if (def) setActiveSceneId(def.id);
      // Post-load processing is NON-FATAL: a throw here (blocked storage,
      // deep-link parsing, etc.) must never blank the loaded viewer.
      try {
        perfRecord("project_config_load", performance.now() - tStart, {
          scenes: s.data.scenes.length, transitions: s.data.transitions.length,
        });
        // Language preference: URL param > localStorage > project default
        const urlLang = new URLSearchParams(window.location.search).get("lang");
        const stored = safeGet(`viewer:${slug}:lang`);
        const supported = s.data.project.supported_languages || ["en"];
        const defLang = s.data.project.default_language || "en";
        const chosen = [urlLang, stored].find(l => l && supported.includes(l)) || defLang;
        setLang(chosen);
        api.post("/public/events", { project_slug: slug, event_type: "project_view", session_id: sessionIdRef.current }).catch(() => {});
        // Deep-link handling: ?unit=A-203 jumps straight into Unit Detail
        // and ?shortlist=A-101,B-301 prefills the buyer's shortlist.
        if (urlShortlist) {
          const codes = urlShortlist.split(",").map((x) => x.trim()).filter(Boolean);
          if (codes.length) {
            const key = `evb.shortlist.${slug}`;
            const existing = JSON.parse(safeGet(key) || "[]");
            const merged = Array.from(new Set([...existing, ...codes]));
            safeSet(key, JSON.stringify(merged));
            window.dispatchEvent(new CustomEvent("evb-shortlist-change", { detail: { slug, codes: merged } }));
          }
        }
        if (urlUnitCode) {
          const target = (i.data.entities || []).find((u) => u.code === urlUnitCode);
          if (target) { setSelectedUnitId(target.id); setMode("unit"); }
        } else if (urlShortlist) {
          setMode("shortlist");
        }
      } catch { /* non-fatal post-load processing */ }
    })();
  }, [slug]);

  useEffect(() => {
    if (!structure || !lang) return;
    try { localStorage.setItem(`viewer:${slug}:lang`, lang); } catch { /* blocked storage (Safari private) */ }
  }, [lang, slug, structure]);

  useDocumentLocale(lang || "en");

  // Refresh inventory periodically
  useEffect(() => {
    if (!structure) return;
    const id = setInterval(async () => {
      try { const i = await api.get(`/public/projects/${slug}/inventory`); setInventory(i.data); } catch (_) { /* ignore */ }
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [slug, structure]);

  // The visible media owns loading. Duplicating it in hidden preloaders
  // competed with startup, especially on phones and cold server caches.
  useScenePrewarm(structure, activeSceneId, buildEffectiveTransitions,
    readySceneId === activeSceneId && mode === "tour" && !pendingScrub);

  // iOS autoplay gesture-unlock + branded "Tap to begin" overlay.
  const { needsGesture, dismiss: dismissGesture } = useAutoplayUnlock({
    sniffMs: AUTOPLAY_SNIFF_MS,
    enabled: mode === "tour" && !pendingScrub && !!activeSceneId,
  });

  // First-scene loading gate: keep it SHORT. We only want to hide a half-
  // decoded video flash; we never want to make the user wait for a long
  // CDN fetch. 2.5s safety — past that we trust the SceneMedia component
  // to do its own poster→video swap without help from this gate.
  // Deep-link routes — `/p/:slug?unit=…` / `/p/:slug?shortlist=…` /
  // `/p/:slug/etap/:code` — should never wait behind the cinematic intro
  // because the user landed on a content panel, not the tour. Hide the
  // loading splash immediately so the sales-share link feels instant.
  const skipIntroForDeepLink = !!(urlUnitCode || urlShortlist || etapCode);
  const [introHidden, setIntroHidden] = useState(skipIntroForDeepLink);
  useEffect(() => {
    if (!structure || !activeSceneId) return;
    if (readySceneId === activeSceneId) { setIntroHidden(true); return; }
    const safety = setTimeout(() => setIntroHidden(true), 2500);
    return () => clearTimeout(safety);
  }, [structure, activeSceneId, readySceneId]);

  // Phase 4 — Presentation Scenarios. Each project may have multiple
  // scenarios; each scenario is an ordered list of steps. Legacy projects
  // with no scenarios are treated as a single "Default" scenario built
  // from the flat presentation_steps list.
  const allSteps = structure?.presentation_steps || [];
  const scenarios = useMemo(() => {
    const raw = structure?.presentation_scenarios || [];
    if (raw.length > 0) {
      return raw.map((sc) => ({
        ...sc,
        steps: allSteps
          .filter((s) => s.scenario_id === sc.id)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
      }));
    }
    if (allSteps.length === 0) return [];
    return [{
      id: "__legacy__", name: "Presentation", is_default: true,
      steps: [...allSteps].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    }];
  }, [structure?.presentation_scenarios, allSteps]);

  const playableScenarios = scenarios.filter((sc) => sc.steps.length > 0);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [scenarioPickerOpen, setScenarioPickerOpen] = useState(false);
  const activeScenarioObj = activeScenarioId ? playableScenarios.find((sc) => sc.id === activeScenarioId) : null;
  const presentationStepsForRun = activeScenarioObj?.steps || [];
  // Highlight target set by the active step (Phase 4 spotlight). Cleared
  // when presentation exits or step has no highlight.
  const [highlight, setHighlight] = useState(null); // {kind, id} where kind = etap|block|unit
  // Locate-a-unit UX state (Phase: Fix Unit Locate)
  const [locateModal, setLocateModal] = useState(null); // { unit, locations:[] }
  const [locateCard, setLocateCard] = useState(null);   // { unit, ts }
  const [floorLocate, setFloorLocate] = useState(null); // { code, floor, blockId, ts }

  // Auto-dismiss the locate mini-card + spotlight after a few seconds so the
  // scene returns to its normal cinematic state.
  useEffect(() => {
    if (!locateCard) return;
    const t = setTimeout(() => { setLocateCard(null); setHighlight((h) => (h?.kind === "unit" ? null : h)); }, 7000);
    return () => clearTimeout(t);
  }, [locateCard]);

  // Phase 4 — Auto-advance timer. When the current step has auto_advance
  // set and a positive duration_seconds, schedule the next step. (Hook
  // must live above the early returns — Rules of Hooks.)
  useEffect(() => {
    if (presentationIndex == null) return;
    const step = presentationStepsForRun[presentationIndex];
    if (!step || !step.auto_advance) return;
    const dur = Math.max(1, Number(step.duration_seconds) || 6) * 1000;
    const isLast = presentationIndex === presentationStepsForRun.length - 1;
    if (isLast) return; // last step never auto-advances
    const timer = setTimeout(() => advancePresentationRef.current?.(+1), dur);
    return () => clearTimeout(timer);
  }, [presentationIndex, activeScenarioId, presentationStepsForRun]);
  // Ref-indirection so the useEffect above can call the function declared
  // further down without a Rules-of-Hooks violation.
  const advancePresentationRef = useRef(null);

  if (error === "unavailable") return <Unavailable lang={lang || "en"} />;
  if (error === "not_found") return <Unavailable lang={lang || "en"} customMsg={L(lang || "en", "project_not_found")} />;
  if (!structure || !inventory || !lang) return <Loading lang={lang || "en"} />;

  const activeScene = structure.scenes.find(s => s.id === activeSceneId) || structure.scenes[0];
  // One decorative backdrop persists throughout the tour. Replacing the
  // source blur with the target blur at each video end flashes most of a phone
  // screen; keeping this layer also avoids overlapping full-screen filters.
  const backdropScene = structure.scenes.find(scene => scene.is_default) || structure.scenes[0];
  const transitionsFrom = buildEffectiveTransitions(structure.transitions, activeSceneId);
  const sceneZones = structure.zones.filter(z => z.scene_id === activeSceneId);

  // Phase 5B — Floor/Stack View eligibility. We show the FLOORS pill only
  // when at least one visible block opts into it (or auto-qualifies via
  // having units with valid floor data).
  // `floor` may live either on the dedicated column or inside the dynamic
  // `fields.Floor` bag — accept both for back-compat with legacy seeds.
  const readFloor = (e) => {
    if (e?.floor != null) return Number(e.floor);
    const f = e?.fields?.Floor ?? e?.fields?.floor;
    if (f == null || f === "") return null;
    const n = Number(f);
    return Number.isFinite(n) ? n : null;
  };
  const hasFloorStack = (() => {
    if (!inventory) return false;
    const blocks = inventory.groups.filter(g => g.group_type === "block" && g.visibility !== false);
    for (const b of blocks) {
      if (b.selection_mode === "none") continue;
      const blockUnits = inventory.entities.filter(e => e.group_id === b.id && e.visibility !== false);
      const hasFloors = blockUnits.some(e => readFloor(e) != null);
      if (b.selection_mode === "floor_stack" || b.selection_mode === "both") return true;
      if (hasFloors) return true;
    }
    return false;
  })();

  // Phase 4 — Compute which zones to spotlight given the active highlight.
  // For etap/block we match the zones whose linked unit lives under that
  // group; for unit we match by entity_id directly. Computed inline (cheap;
  // depends on values only available after the early returns above).
  let highlightZoneIds = null;
  if (highlight) {
    const ids = new Set();
    if (highlight.kind === "unit") {
      for (const z of sceneZones) {
        if (z.entity_id === highlight.id) ids.add(z.id);
      }
    } else if (highlight.kind === "block") {
      for (const z of sceneZones) {
        const ent = z.entity_id ? inventory.entities.find(e => e.id === z.entity_id) : null;
        if ((ent && ent.group_id === highlight.id) || z.group_id === highlight.id) ids.add(z.id);
      }
    } else if (highlight.kind === "etap") {
      const blockIds = new Set(inventory.groups.filter(g => g.parent_group_id === highlight.id).map(g => g.id));
      for (const z of sceneZones) {
        const ent = z.entity_id ? inventory.entities.find(e => e.id === z.entity_id) : null;
        if (ent && blockIds.has(ent.group_id)) ids.add(z.id);
        if (z.group_id && (blockIds.has(z.group_id) || z.group_id === highlight.id)) ids.add(z.id);
      }
    }
    highlightZoneIds = ids;
  }

  // Freeze an on-screen video/canvas frame into a displayable canvas. A
  // cross-origin source taints the canvas (no toDataURL) but a tainted
  // canvas still *renders* — all we need for a seamless crossfade.
  const captureFrameFrom = captureVideoFrame;
  const captureSceneFrame = () => {
    // Rapid navigation may replace a destination that is still decoding.
    // Preserve the frame actually on screen, not that hidden destination.
    if (transitionState?.frozenFrame && !transitionState.fading) return transitionState.frozenFrame;
    return captureFrameFrom(document.querySelector('[data-scene-canvas] video[data-testid="scene-media-video"]'));
  };

  const captureTransitionFrame = () => captureFrameFrom(
    document.querySelector('[data-testid="viewer-scrub-canvas"][data-frame-ready="true"]')
      || document.querySelector('[data-testid="viewer-scrub-video"], [data-testid="viewer-scrub-canvas"]')
  );
  const goToScene = (toId, dir = "left", displayedFrame = null) => {
    if (pendingScrubRef.current || !toId || toId === activeSceneId) return;
    const frozenFrame = displayedFrame || captureSceneFrame();
    setFailedSceneId(null);
    setPreviousSceneId(activeSceneId);
    setTransitionState({ fromId: activeSceneId, toId, dir, frozenFrame });
    trackEvent("scene_transition", { metadata: { to: toId, direction: dir } });
    // Phase 1 — Panorama 360 MVP. Emit panorama_open when entering a
    // panorama scene; the unit-360-tab path emits unit_360_open in
    // UnitDetail instead.
    const target = structure?.scenes.find(s => s.id === toId);
    if (target?.type === "panorama_360") {
      trackEvent("panorama_open", { scene_id: toId });
    }
    // Mount the destination under the opaque outgoing frame. Its displayed
    // callback begins the crossfade only once the destination can be shown.
    setActiveSceneId(toId);
  };

  // ── Locate a unit in its visual location(s) ──────────────────────────
  const applyLocation = (loc, unit) => {
    setLocateModal(null);
    trackEvent("unit_locate_success", { entity_id: unit.id, metadata: { type: loc.type, scene_id: loc.sceneId } });
    if (loc.type === "floor") {
      setFloorLocate({ code: unit.code, floor: loc.floor, blockId: loc.blockId, ts: Date.now() });
      setMode("floors");
      return;
    }
    // zone / facade / scene → navigate to the scene, spotlight the unit,
    // and surface a small mini-card.
    setMode("tour");
    setHighlight({ kind: "unit", id: unit.id });
    if (loc.sceneId && loc.sceneId !== activeSceneId) goToScene(loc.sceneId);
    setLocateCard({ unit, ts: Date.now() });
  };

  const handleLocateUnit = (unit) => {
    if (!unit) return;
    trackEvent("unit_locate_click", { entity_id: unit.id });
    const locations = resolveUnitLocations(unit, structure, inventory);
    if (locations.length === 0) {
      trackEvent("unit_locate_missing_location", { entity_id: unit.id });
      toast("This unit does not have a visual location yet.", {
        description: `${unit.code}${unit.name && unit.name !== unit.code ? " · " + unit.name : ""}`,
      });
      return;
    }
    if (locations.length === 1) { applyLocation(locations[0], unit); return; }
    setLocateModal({ unit, locations });
  };

  // Centralised helper: every time we *fall back* to a fade (instead of
  // running a scrub transition) we log a strict reason. This is the only
  // path that takes us to a fade — search for `goToScene` and you'll find
  // it always preceded by a logFallback() call when the user expected a
  // scrubbable transition.
  //
  // The diagnostic payload now includes: from/to scene IDs, direction,
  // transition ID (if known), video readyState/networkState/currentTime/
  // duration, prewarm cache state, and a firstUse flag — exactly what the
  // P0 stabilization brief asks every fallback to surface.
  const fadeFallback = (toId, dir, reason, details = {}, displayedFrame = null) => {
    const store = (typeof window !== "undefined" && window.__viewerPerf) || {};
    const prewarms = store.prewarms || {};
    const tId = details.transitionId;
    const prewarm = tId ? prewarms[tId] : null;
    const enriched = {
      transitionId: tId || null,
      from: activeSceneId,
      to: toId,
      dir,
      cached: prewarm ? prewarm.status === "hit" : null,
      prewarm_status: prewarm?.status || "unknown",
      prewarm_ms: prewarm?.ms ?? null,
      ...details,
    };
    logFallback(reason, enriched);
    goToScene(toId, dir, displayedFrame);
  };

  // When a scrubbable transition fails (arm timeout, playback error, …)
  // the overlay calls this with a reason + diagnostic details; we
  // complete the transition as a fade so the user still arrives at the
  // destination scene, while the details land in window.__viewerPerf so
  // QA can tell a real timeout from a missed metadata event.
  const handleScrubFailedFallback = (reason, details = {}) => {
    if (!pendingScrub || pendingScrubRef.current !== pendingScrub) return;
    const shouldCommit = pendingScrub.auto || scrubReleaseTargetRef.current?.commit === true;
    // An error cannot turn an uncommitted or cancelled drag into navigation.
    const displayedFrame = shouldCommit ? captureTransitionFrame() : null;
    const toId = pendingScrub.toId;
    const dir = pendingScrub.transition?.swipe_direction || "left";
    pendingScrubRef.current = null;
    setPendingScrub(null);
    setLiveScrubProgress(null);
    setScrubReleaseTarget(null);
    if (!shouldCommit) return;
    fadeFallback(toId, dir, reason, {
      transitionId: pendingScrub.transition?.id,
      reverse: !!pendingScrub.transition?._reverse,
      ...details,
    }, displayedFrame);
  };

  const goOverview = () => {
    const def = structure.scenes.find(s => s.is_default) || structure.scenes[0];
    if (def && def.id !== activeSceneId) goToScene(def.id);
  };

  // Phase 4 — apply a presentation step's target to viewer state.
  const applyStep = (step) => {
    if (pendingScrubRef.current) return;
    if (!step) return;
    const t = step.target_type;
    // Clear stale highlight from a previous step.
    setHighlight(null);
    if (t === "scene" || t === "panorama_scene" || t === "location" || t === "amenity") {
      if (step.target_id) {
        goToScene(step.target_id);
        setMode("tour");
      }
      return;
    }
    if (t === "etap" || t === "block") {
      setListState(prev => ({ ...prev, scope: { type: t, id: step.target_id } }));
      setMode("list");
      return;
    }
    if (t === "highlight_etap") {
      if (step.target_id) setHighlight({ kind: "etap", id: step.target_id });
      setMode("tour");
      return;
    }
    if (t === "highlight_block") {
      if (step.target_id) setHighlight({ kind: "block", id: step.target_id });
      setMode("tour");
      return;
    }
    if (t === "highlight_unit") {
      if (step.target_id) setHighlight({ kind: "unit", id: step.target_id });
      setMode("tour");
      return;
    }
    if (t === "unit" || t === "unit_plan" || t === "unit_gallery"
        || t === "open_unit_360" || t === "open_payment_plan") {
      if (!step.target_id) return;
      setSelectedUnitId(step.target_id);
      setUnitOpenSource("tour");
      setUnitInitialTab(
        t === "unit_plan" ? "plan"
        : t === "unit_gallery" ? "gallery"
        : t === "open_unit_360" ? "tour360"
        : t === "open_payment_plan" ? "payment"
        : "info"
      );
      setMode("unit");
      trackEvent("unit_detail_open", { entity_id: step.target_id, source: "presentation" });
      return;
    }
    if (t === "open_units_list") { setListState(prev => ({ ...prev, scope: { type: "all" } })); setMode("list"); return; }
    if (t === "open_shortlist") { setMode("shortlist"); return; }
    if (t === "info_slide") { setMode("tour"); return; }
  };

  const startPresentation = (scenarioId = null) => {
    if (!playableScenarios.length) return;
    // If only one playable scenario, skip the picker. Otherwise show it
    // unless the caller passed a specific scenario.
    let chosenId = scenarioId;
    if (!chosenId) {
      if (playableScenarios.length === 1) chosenId = playableScenarios[0].id;
      else { setScenarioPickerOpen(true); return; }
    }
    const sc = playableScenarios.find((x) => x.id === chosenId);
    if (!sc) return;
    setActiveScenarioId(chosenId);
    setPresentationIndex(0);
    setScenarioPickerOpen(false);
    applyStep(sc.steps[0]);
    trackEvent("presentation_start", {
      steps: sc.steps.length,
      metadata: { scenario_id: chosenId, scenario_name: sc.name },
    });
  };
  const advancePresentation = (dir) => {
    if (pendingScrubRef.current) return;
    setPresentationIndex((idx) => {
      if (idx == null) return idx;
      const next = idx + dir;
      if (next < 0 || next >= presentationStepsForRun.length) return idx;
      applyStep(presentationStepsForRun[next]);
      trackEvent("presentation_step", { step: next, metadata: { scenario_id: activeScenarioId } });
      return next;
    });
  };
  const exitPresentation = () => {
    setPresentationIndex(null);
    setActiveScenarioId(null);
    setUnitInitialTab(null);
    setHighlight(null);
    trackEvent("presentation_exit");
  };

  // Bridge to the auto-advance useEffect above the early returns.
  advancePresentationRef.current = advancePresentation;

  // Back-compat alias used by the existing JSX below this block.
  const presentationSteps = presentationStepsForRun;
  // Phase 6 — location map availability (needs enabled + a center or content).
  const locationMap = structure?.project?.location_map || null;
  const locationMapEnabled = !!(locationMap && locationMap.enabled &&
    (Array.isArray(locationMap.landmarks) && locationMap.landmarks.length > 0 ||
     Array.isArray(locationMap.routes) && locationMap.routes.length > 0 ||
     Array.isArray(locationMap.center)));

  // Phase 1 — Panorama 360 MVP. Apply a hotspot's action.
  const panoramaHotspots = structure.panorama_hotspots || [];
  const hotspotsForActiveScene = activeScene?.type === "panorama_360"
    ? panoramaHotspots.filter(h => h.panorama_scene_id === activeScene.id)
    : [];
  const onPanoramaHotspot = (h) => {
    if (pendingScrubRef.current) return;
    trackEvent("panorama_hotspot_click", { entity_id: h.target_id, metadata: { hotspot_id: h.id, action_type: h.action_type } });
    if (h.action_type === "go_to_panorama" && h.target_id) {
      const target = structure.scenes.find(s => s.id === h.target_id);
      if (target) { setMode("tour"); goToScene(target.id); trackEvent("panorama_open", { scene_id: target.id }); }
      return;
    }
    if (h.action_type === "go_to_scene" && h.target_id) {
      setMode("tour"); goToScene(h.target_id); return;
    }
    if (h.action_type === "open_unit" && h.target_id) {
      const unit = inventory.entities.find(e => e.id === h.target_id);
      if (unit) {
        setSelectedUnitId(unit.id);
        setUnitOpenSource("tour");
        setMode("unit");
        trackEvent("unit_detail_open", { entity_id: unit.id, source: "panorama_hotspot" });
      }
      return;
    }
    if (h.action_type === "show_info") {
      const text = key => h.translations?.[key]?.[lang] || h.translations?.[key]?.[structure.project.default_language] || tr(h, key, lang, structure.project.default_language) || "";
      setZoneInfo({ title: text("label"), description: text("description"), image_url: h.image_url || null });
    }
  };

  const onZoneClick = (z) => {
    if (pendingScrubRef.current) return;
    const action = z.click_action || "open_details";
    if (action === "show_info") {
      trackEvent("zone_click", { metadata: { zone_id: z.id, action } });
      setZoneInfo({
        title: tr(z, "info_title", lang, structure.project.default_language) || tr(z, "name", lang, structure.project.default_language) || z.name,
        description: tr(z, "info_description", lang, structure.project.default_language) || "",
        image_url: z.info_image_url || null,
      });
      return;
    }
    if (action === "goto_scene" && z.target_scene_id) {
      trackEvent("zone_click", { metadata: { zone_id: z.id, action, target: z.target_scene_id } });
      // Prefer a scrubbable transition between current scene and target scene
      // if one exists in the project — that gives us the smooth 3D rotate.
      const scrubT = transitionsFrom.find(t =>
        t.scrubbable_enabled && t.transition_media_url && t.to_scene_id === z.target_scene_id);
      if (scrubT) {
        startAutoVideo(scrubT);
        return;
      }
      const autoT = transitionsFrom.find(t =>
        t.transition_type === "auto_video" && t.transition_media_url && t.to_scene_id === z.target_scene_id);
      if (autoT) { startAutoVideo(autoT); return; }
      // Identify exactly *why* this is degrading to a fade so QA can see
      // it in the console / window.__viewerPerf.fallbacks.
      const anyT = transitionsFrom.find(t => t.to_scene_id === z.target_scene_id);
      const reason = !anyT
        ? FALLBACK_REASONS.NO_TRANSITION_FOR_DIRECTION
        : (!anyT.scrubbable_enabled && anyT.transition_type !== "auto_video"
            ? FALLBACK_REASONS.UNSUPPORTED_SCRUB
            : FALLBACK_REASONS.MISSING_TRANSITION_MEDIA);
      fadeFallback(z.target_scene_id, "left", reason, { source: "zone_click" });
      return;
    }
    // Default: open the linked unit / group panel (back-compat behaviour)
    if (z.zone_type === "entity" && z.entity_id) {
      trackEvent("zone_click", { entity_id: z.entity_id, metadata: { zone_id: z.id } });
      setSelectedUnitId(z.entity_id);
      setUnitOpenSource("tour");
      setMode("unit");
    } else if (z.zone_type === "group" && z.group_id) {
      trackEvent("zone_click", { metadata: { group_id: z.group_id } });
      const group = inventory.groups.find(g => g.id === z.group_id);
      setListState(prev => ({ ...prev, scope: { type: group?.group_type === "etap" ? "etap" : "block", id: z.group_id } }));
      setMode("list");
    }
  };

  // Find a scrubbable transition for the current scene matching swipe direction
  const findScrubbable = (dir) =>
    transitionsFrom.find(t => t.scrubbable_enabled && t.transition_media_url && t.swipe_direction === dir);
  // Find an auto-video transition (plays media on swipe without user scrub).
  const findAutoVideo = (dir) =>
    transitionsFrom.find(t => t.transition_type === "auto_video" && t.transition_media_url && t.swipe_direction === dir);
  // Helper: mount an auto-video transition. Reuses ScrubOverlay with the
  // `autoTween` flag so the same FSM, prewarm, and fallback paths apply.
  const startAutoVideo = (t) => {
    if (pendingScrubRef.current) return;
    setScrubReleaseTarget(null);
    const next = { transition: t, fromId: activeSceneId, toId: t.to_scene_id, auto: true };
    pendingScrubRef.current = next;
    setPendingScrub(next);
    setLiveScrubProgress(0);
  };
  // Buttons and keyboard arrows use one navigation path, including prepared
  // video playback, the pending-transition lock, and normal fade fallback.
  const navigateTransition = (transition, source = "bottom_nav_button") => {
    if (pendingScrubRef.current || !transition) return false;
    if ((transition.scrubbable_enabled || transition.transition_type === "auto_video") && transition.transition_media_url) {
      startAutoVideo(transition);
    } else {
      const reason = !transition.scrubbable_enabled
        ? FALLBACK_REASONS.MANUAL_FALLBACK
        : FALLBACK_REASONS.MISSING_TRANSITION_MEDIA;
      fadeFallback(transition.to_scene_id, transition.swipe_direction, reason, {
        source, transitionId: transition.id,
      });
    }
    return true;
  };
  const projectName = tr(structure.project, "name", lang, structure.project.default_language) || structure.project.name;

  return (
    <div className={`viewer-shell relative w-screen ${embed ? "h-screen" : "h-screen"} overflow-hidden select-none`} data-testid="public-viewer">
      <LetterboxBackdrop scene={backdropScene} project={structure.project} />
      {/* First-scene loading gate — covers everything below until the active
          scene's media is ready (or a 2.5s safety timeout elapses). */}
      {!introHidden && (
        <div className="absolute inset-0 z-50 bg-black flex items-center justify-center fade-in" data-testid="viewer-intro-loading">
          <div className="flex flex-col items-center gap-4">
            <div className="font-display text-2xl text-white/90 tracking-tight">{projectName}</div>
            <div className="flex items-center gap-2 text-white/55 text-[11px] uppercase tracking-[0.3em]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {L(lang, "loading")}
            </div>
          </div>
        </div>
      )}

      {/* Scrubbable transition overlay (top-most when active) */}
      {pendingScrub && (
        <ScrubOverlay
          backdropManaged
          key={`${pendingScrub.transition.id}:${pendingScrub.fromId}:${pendingScrub.toId}:${!!pendingScrub.transition._reverse}:${!!pendingScrub.auto}`}
          transition={pendingScrub.transition}
          fromScene={structure.scenes.find(s => s.id === pendingScrub.fromId)}
          toScene={structure.scenes.find(s => s.id === pendingScrub.toId)}
          reverse={!!pendingScrub.transition._reverse}
          project={structure.project}
          controlledProgress={liveScrubProgress}
          releaseSignal={scrubReleaseTarget}
          autoTween={!!pendingScrub.auto}
          onComplete={(presentedFrame = null) => {
            if (pendingScrubRef.current !== pendingScrub) return false;
            trackEvent("scene_transition", { metadata: { to: pendingScrub.toId, mode: "scrub" } });
            // Crossfade out of the overlay's final frame so the incoming
            // scene never flashes its poster / a dark dip on overlay unmount.
            // Native playback hands over the same canvas already painted over
            // its endpoint. Do not sample a video during decoder teardown.
            const frozenFrame = presentedFrame || captureTransitionFrame();
            if (frozenFrame) {
              setPreviousSceneId(pendingScrub.fromId);
              setTransitionState({ fromId: pendingScrub.fromId, toId: pendingScrub.toId, dir: null, frozenFrame });
            }
            setActiveSceneId(pendingScrub.toId);
            pendingScrubRef.current = null;
            setPendingScrub(null);
            setLiveScrubProgress(null);
            setScrubReleaseTarget(null);
            return true;
          }}
          onCancel={() => {
            if (pendingScrubRef.current !== pendingScrub) return;
            pendingScrubRef.current = null;
            setPendingScrub(null);
            setLiveScrubProgress(null);
            setScrubReleaseTarget(null);
          }}
          onFailedFallback={handleScrubFailedFallback}
        />
      )}

      {/* Scene canvas with gesture handler */}
      <SceneCanvas
        backdropManaged
        coveredByScrub={!!pendingScrub}
        active={mode === "tour" && !pendingScrub}
        fieldDefinitions={structure.field_definitions || []}
        scene={activeScene}
        prevScene={transitionState ? structure.scenes.find(s => s.id === transitionState.fromId) : null}
        frozenFrame={transitionState?.frozenFrame || null}
        transitioning={!!transitionState || failedSceneId === activeSceneId}
        fadeOutgoing={!!transitionState?.fading}
        zones={sceneZones}
        inventory={inventory}
        scenes={structure.scenes}
        project={structure.project}
        lang={lang}
        defaultLang={structure.project.default_language}
        panoramaHotspots={hotspotsForActiveScene}
        onPanoramaHotspot={onPanoramaHotspot}
        onZoneClick={onZoneClick}
        highlightZoneIds={highlightZoneIds}
        onScrubProgress={setLiveScrubProgress}
        onScrubRelease={(commit) => {
          // Commit/cancel is signalled to the ScrubOverlay via a value
          // change. Use a fresh object so React re-fires the effect even
          // if the same commit value (e.g. another cancel) lands twice.
          const release = { commit: !!commit, ts: performance.now() };
          scrubReleaseTargetRef.current = release;
          setScrubReleaseTarget(release);
        }}
        onHorizontalSwipe={(dir) => {
          if (pendingScrubRef.current) return false;
          // First-touch hand-off priority:
          //   1. Scrubbable transition in this direction → user drives scrub.
          //   2. Auto-video transition in this direction → media plays itself.
          //   3. Plain transition → log fallback + fade.
          const scrubT = findScrubbable(dir);
          if (scrubT) {
            setScrubReleaseTarget(null);
            const next = { transition: scrubT, fromId: activeSceneId, toId: scrubT.to_scene_id };
            pendingScrubRef.current = next;
            setPendingScrub(next);
            setLiveScrubProgress(0);
            return true;
          }
          const autoT = findAutoVideo(dir);
          if (autoT) {
            startAutoVideo(autoT);
            return true;
          }
          const t = transitionsFrom.find(tr => tr.swipe_direction === dir);
          if (t) {
            const reason = !t.scrubbable_enabled && t.transition_type !== "auto_video"
              ? FALLBACK_REASONS.UNSUPPORTED_SCRUB
              : (!t.transition_media_url
                  ? FALLBACK_REASONS.MISSING_TRANSITION_MEDIA
                  : FALLBACK_REASONS.NO_TRANSITION_FOR_DIRECTION);
            fadeFallback(t.to_scene_id, dir, reason, { source: "swipe" });
            return true;
          }
          logFallback(FALLBACK_REASONS.NO_TRANSITION_FOR_DIRECTION, {
            dir, from: activeSceneId, source: "swipe", noTransitionAtAll: true,
          });
          return false;
        }}
        onActiveMediaStatus={debugVisible ? setVideoStatus : undefined}
        onActiveMediaReady={setReadySceneId}
        onActiveMediaDisplayed={beginSceneFade}
        onActiveMediaDisplayFailed={handleMediaDisplayFailure}
      />

      {/* One viewport-sized tone layer covers both media paths, below UI. */}
      <div className="absolute inset-0 z-20 pointer-events-none" aria-hidden="true"
        data-testid="viewer-media-vignette" style={{ background: MEDIA_VIGNETTE }} />

      {!pendingScrub && failedSceneId === activeSceneId && (
        <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center"
          data-testid="viewer-media-unavailable" role="status">
          <div className="rounded-xl bg-black/70 px-5 py-3 text-sm text-white/85">{L(lang, "media_unavailable")}</div>
        </div>
      )}

      {/* Admin / preview-only debug HUD. shouldShowDebugPanel() guards public users. */}
      {debugVisible && (
        <ViewerDebugPanel
          scene={activeScene}
          status={videoStatus}
          transitionMode={transitionState ? "fade" : (pendingScrub ? "scrub" : null)}
          pendingTransitionId={pendingScrub?.transition?.id}
        />
      )}

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 px-3 md:px-6 pt-3 md:pt-5 flex flex-col items-start gap-2 pointer-events-none">
        <div className="flex items-center gap-3 pointer-events-auto">
          <div className="h-2.5 w-2.5 rounded-full" style={{ background: structure.project.accent_color || "#C4A47C" }} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">{L(lang, "loading") ? "" : ""}</div>
            <div className="font-display text-lg md:text-xl text-white tracking-tight" data-testid="viewer-project-name">{projectName}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pointer-events-auto max-w-full">
          <LanguageSwitcher current={lang} supported={structure.project.supported_languages || ["en"]} onChange={(l) => { setLang(l); trackEvent("language_change", { metadata: { lang: l } }); }} />
          {playableScenarios.length > 0 && presentationIndex == null && (
            <button
              onClick={() => startPresentation()}
              data-testid="viewer-start-presentation" disabled={!!pendingScrub}
              className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
            >
              <Play className="h-4 w-4" /> {L(lang, "start_presentation")}
            </button>
          )}
          <ShowroomButton slug={slug} lang={lang} />
          <AmbientAudio
            lang={lang}
            url={structure.project.background_audio_url}
            volume={structure.project.background_audio_volume ?? 0.35}
            slug={slug}
            sceneAudioUrl={activeScene?.background_audio_url || null}
            sceneAudioVolume={activeScene?.background_audio_volume}
          />
          <button
            onClick={() => { trackEvent("units_list_open"); setMode("list"); }}
            data-testid="viewer-open-units" disabled={!!pendingScrub}
            className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
          >
            <List className="h-4 w-4" /> {L(lang, "units")}
          </button>
          {hasFloorStack && (
            <button
              onClick={() => { trackEvent("floor_stack_open", { source: "topbar" }); setFloorLocate(null); setMode("floors"); }}
              data-testid="viewer-open-floors" disabled={!!pendingScrub}
              className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
            >
              <Layers className="h-4 w-4" /> {L(lang, "floors")}
            </button>
          )}
          {/* Smart Unit Finder (SF-1) */}
          <button
            onClick={() => { trackEvent("unit_filter_open", { source: "topbar" }); setMode("finder"); }}
            data-testid="viewer-open-finder" disabled={!!pendingScrub}
            className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
          >
            <Filter className="h-4 w-4" /> {L(lang, "find")}
          </button>
          {locationMapEnabled && (
            <button
              onClick={() => { trackEvent("location_map_open", { source: "topbar" }); setLocationOpen(true); }}
              data-testid="viewer-open-location" disabled={!!pendingScrub}
              className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
            >
              <MapPin className="h-4 w-4" /> {L(lang, "location")}
            </button>
          )}
          {shortlistCodes.length > 0 && (
            <button
              onClick={() => { trackEvent("shortlist_open"); setMode("shortlist"); }}
              data-testid="viewer-open-shortlist" disabled={!!pendingScrub}
              className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
            >
              <Bookmark className="h-4 w-4 fill-white/20" /> {L(lang, "shortlist")}
              <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full" data-testid="shortlist-count">{shortlistCodes.length}</span>
            </button>
          )}
          {structure.project.contact_config?.whatsapp && (
            <a
              href={waLink(structure.project, null, lang)}
              target="_blank" rel="noreferrer"
              onClick={() => trackEvent("whatsapp_click")}
              data-testid="viewer-whatsapp-top"
              className="pill-nav inline-flex items-center gap-2 text-white text-xs tracking-wider uppercase px-4 py-2 rounded-full hover:bg-white/10 disabled:opacity-40 disabled:cursor-wait transition"
            >
              <MessageCircle className="h-4 w-4" /> {L(lang, "contact")}
            </a>
          )}
        </div>
      </div>

      <SceneNavigation
        scene={activeScene}
        transitions={transitionsFrom}
        scenes={structure.scenes}
        project={structure.project}
        lang={lang}
        onNavigate={navigateTransition}
        onOverview={goOverview}
        busy={!!pendingScrub}
        keyboardEnabled={mode === "tour" && introHidden && !needsGesture && !transitionState
          && presentationIndex === null && !locationOpen && !scenarioPickerOpen
          && !locateModal && !zoneInfo}
      />

      {/* Modes */}
      {mode === "list" && (
        <UnitsList
          state={listState} onStateChange={setListState}
          fieldDefinitions={structure.field_definitions || []}
          inventory={inventory}
          project={structure.project}
          onClose={() => setMode("tour")}
          onPickUnit={(id) => { setSelectedUnitId(id); setUnitOpenSource("list"); setMode("unit"); trackEvent("unit_detail_open", { entity_id: id }); }}
          onLocate={handleLocateUnit}
          lang={lang}
          defaultLang={structure.project.default_language}
          initialEtapCode={etapCode}
          unitsWithPanorama={new Set(structure.scenes.filter(s => s.type === "panorama_360" && s.panorama_parent_unit_id).map(s => s.panorama_parent_unit_id))}
        />
      )}
      {mode === "floors" && (
        <FloorStackView
          fieldDefinitions={structure.field_definitions || []}
          inventory={inventory}
          project={structure.project}
          onClose={() => setMode("tour")}
          onPickUnit={(id) => { setSelectedUnitId(id); setUnitOpenSource("floor_stack"); setMode("unit"); trackEvent("unit_detail_open", { entity_id: id, source: "floor_stack" }); }}
          onTrack={(name, meta) => trackEvent(name, meta || {})}
          locate={floorLocate}
          lang={lang}
          defaultLang={structure.project.default_language}
          unitsWithPanorama={new Set(structure.scenes.filter(s => s.type === "panorama_360" && s.panorama_parent_unit_id).map(s => s.panorama_parent_unit_id))}
          shortlistCodes={shortlistCodes}
          onToggleShortlist={(code) => toggleShortlistGlobal(slug, code)}
        />
      )}
      {mode === "finder" && (
        <UnitFinderDrawer
          state={finderState} onStateChange={setFinderState}
          structure={structure}
          inventory={inventory}
          onClose={() => setMode("tour")}
          onPickUnit={(id) => { setSelectedUnitId(id); setUnitOpenSource("finder"); setMode("unit"); trackEvent("unit_detail_open", { entity_id: id, source: "finder" }); }}
          onLocate={handleLocateUnit}
          onTrack={(name, meta) => trackEvent(name, meta || {})}
          lang={lang}
          defaultLang={structure.project.default_language}
          unitsWithPanorama={new Set(structure.scenes.filter(s => s.type === "panorama_360" && s.panorama_parent_unit_id).map(s => s.panorama_parent_unit_id))}
          shortlistCodes={shortlistCodes}
          onToggleShortlist={(code) => toggleShortlistGlobal(slug, code)}
        />
      )}
      {mode === "unit" && selectedUnitId && (
        <UnitDetail
          key={selectedUnitId}
          fieldDefinitions={structure.field_definitions || []}
          onPanoramaHotspot={onPanoramaHotspot}
          backLabel={L(lang, ["tour", "locate"].includes(unitOpenSource) ? "back_to_tour" : unitOpenSource === "compare" ? "compare" : unitOpenSource === "finder" ? "find_unit" : unitOpenSource === "shortlist" ? "shortlist" : unitOpenSource === "floor_stack" ? "floors" : "all_units")}
          unit={inventory.entities.find(e => e.id === selectedUnitId)}
          group={inventory.groups.find(g => g.id === inventory.entities.find(e => e.id === selectedUnitId)?.group_id)}
          project={structure.project}
          unitPanoramas={structure.scenes.filter(s => s.type === "panorama_360" && s.panorama_parent_unit_id === selectedUnitId)}
          panoramaHotspots={structure.panorama_hotspots || []}
          onClose={() => { setMode("tour"); setUnitInitialTab(null); }}
          onBackToList={() => { setMode(["tour", "locate"].includes(unitOpenSource) ? "tour" : unitOpenSource === "compare" ? "compare" : unitOpenSource === "floor_stack" ? "floors" : unitOpenSource === "shortlist" ? "shortlist" : unitOpenSource === "finder" ? "finder" : "list"); setUnitInitialTab(null); }}
          onLead={() => setMode("lead")}
          onOpenShortlist={() => setMode("shortlist")}
          onLocate={handleLocateUnit}
          onTabOpen={(t) => { journeyRef.current[`${t}_opened`] = true; }}
          onSelectPaymentPlan={(planId) => { const unit = inventory.entities.find(u => u.id === selectedUnitId); if (unit) plansByUnitRef.current[unit.code] = planId; }}
          shortlistCount={shortlistCodes.length}
          slug={slug}
          trackEvent={trackEvent}
          lang={lang}
          defaultLang={structure.project.default_language}
          initialTab={unitInitialTab}
        />
      )}
      {mode === "shortlist" && (
        <ShortlistPanel
          fieldDefinitions={structure.field_definitions || []}
          slug={slug}
          inventory={inventory}
          project={structure.project}
          onClose={() => setMode("tour")}
          onPickUnit={(id) => { setSelectedUnitId(id); setUnitOpenSource("shortlist"); setMode("unit"); trackEvent("unit_detail_open", { entity_id: id, source: "shortlist" }); }}
          onCompare={(codes) => { setCompareCodes(codes); setMode("compare"); trackEvent("compare_open", { count: codes.length, codes }); }}
          onExportPdf={(codes, kind) => downloadBulkPdf(codes, kind)}
          lang={lang}
          defaultLang={structure.project.default_language}
        />
      )}
      {mode === "compare" && (
        <ComparePanel
          fieldDefinitions={structure.field_definitions || []}
          onCodesChange={setCompareCodes}
          slug={slug}
          inventory={inventory}
          project={structure.project}
          codes={compareCodes}
          onClose={() => setMode("tour")}
          onBack={() => setMode("shortlist")}
          onPickUnit={(id) => { setSelectedUnitId(id); setUnitOpenSource("compare"); setMode("unit"); }}
          onLocate={handleLocateUnit}
          onExportPdf={(codes, kind) => downloadBulkPdf(codes, kind)}
          lang={lang}
          defaultLang={structure.project.default_language}
        />
      )}
      {mode === "lead" && (
        <LeadFormPanel
          project={structure.project}
          unit={inventory.entities.find(e => e.id === selectedUnitId)}
          onClose={() => setMode(selectedUnitId ? "unit" : "tour")}
          trackEvent={trackEvent}
          lang={lang}
          journey={{ ...journeyRef.current, selected_payment_plan_id: plansByUnitRef.current[inventory.entities.find(u => u.id === selectedUnitId)?.code] || null }}
          shortlistCodes={shortlistCodes}
          sessionId={sessionIdRef.current}
          activeSceneId={activeSceneId}
        />
      )}

      {/* Info popover for zones with click_action='show_info' */}
      {zoneInfo && (
        <ZoneInfoPanel
          lang={lang}
          title={zoneInfo.title}
          description={zoneInfo.description}
          imageUrl={zoneInfo.image_url}
          onClose={() => setZoneInfo(null)}
        />
      )}

      {/* Phase 4 — Scenario picker (when project has multiple scenarios) */}
      {scenarioPickerOpen && (
        <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={() => setScenarioPickerOpen(false)} data-testid="scenario-picker">
          <div className="w-full max-w-xl bg-zinc-950 border border-white/10 rounded-2xl p-7 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "pick_journey")}</div>
            <h3 className="font-display text-2xl mt-1 mb-5">{L(lang, "start_presentation")}</h3>
            <div className="space-y-2">
              {playableScenarios.map((sc) => (
                <button
                  key={sc.id}
                  onClick={() => startPresentation(sc.id)}
                  className="w-full text-left rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition p-4"
                  data-testid={`scenario-pick-${sc.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-display text-lg">{tr(sc, "name", lang, structure.project.default_language) || sc.name}</div>
                    <div className="text-xs text-white/55">{sc.steps.length} {L(lang, "steps")}</div>
                  </div>
                  {sc.description && (
                    <div className="text-sm text-white/70 mt-1">{tr(sc, "description", lang, structure.project.default_language) || sc.description}</div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-5 flex justify-end">
              <button onClick={() => setScenarioPickerOpen(false)} className="text-white/60 hover:text-white text-xs uppercase tracking-[0.3em]" data-testid="scenario-picker-cancel">{L(lang, "cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 4 — Presentation Mode overlay (guided sequence) */}
      {presentationIndex != null && (
        <PresentationMode
          navigationDisabled={!!pendingScrub}
          steps={presentationSteps}
          currentIndex={presentationIndex}
          scenarioName={activeScenarioObj ? (tr(activeScenarioObj, "name", lang, structure.project.default_language) || activeScenarioObj.name) : null}
          onPrev={() => advancePresentation(-1)}
          onNext={() => advancePresentation(+1)}
          onExit={exitPresentation}
          lang={lang}
          defaultLang={structure.project.default_language}
        />
      )}

      {/* Phase 5 — presentation-only timed audio. Mounted only while a
          presentation is running so it can never bleed into normal browsing. */}
      {presentationIndex != null && (
        <PresentationAudio
          url={presentationSteps[presentationIndex]?.audio_url || null}
          volume={presentationSteps[presentationIndex]?.audio_volume ?? 0.6}
          fade={presentationSteps[presentationIndex]?.audio_fade_seconds ?? 1.2}
          loop={presentationSteps[presentationIndex]?.audio_loop !== false}
        />
      )}

      {/* Phase 6 — Location map full-screen overlay */}
      {locationOpen && locationMap && (
        <LocationMap locationMap={locationMap} lang={lang} onClose={() => setLocationOpen(false)} />
      )}

      {/* Locate — multi-location chooser */}
      {locateModal && (
        <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6 fade-in" onClick={() => setLocateModal(null)} data-testid="locate-modal">
          <div className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl p-6 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/55">{L(lang, "show_unit_in")}</div>
            <h3 className="font-display text-xl mt-1 mb-4">{locateModal.unit.code}</h3>
            <div className="space-y-2">
              {locateModal.locations.map((loc, idx) => (
                <button
                  key={idx}
                  onClick={() => applyLocation(loc, locateModal.unit)}
                  className="w-full text-left rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition px-4 py-3 flex items-center justify-between"
                  data-testid={`locate-option-${loc.type}`}
                >
                  <span className="font-medium">{loc.sceneName || L(lang, loc.type === "floor" ? "floor_plan" : "view")}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#C4A47C]">{L(lang, ({ zone: "building_view", facade: "facade_view", scene: "tour_360", floor: "floor_plan_view" })[loc.type] || "view")}</span>
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setLocateModal(null)} className="text-white/60 hover:text-white text-xs uppercase tracking-[0.3em]" data-testid="locate-modal-cancel">{L(lang, "cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Locate — spotlight mini-card shown over the scene */}
      {locateCard && mode === "tour" && (() => {
        const u = locateCard.unit;
        return (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-28 z-40 fade-in" data-testid="locate-mini-card">
            <div className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-4 border border-[#C4A47C]/40 shadow-xl">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C4A47C]">{L(lang, "located")}</div>
                <div className="font-display text-lg text-white leading-tight">{u.code}</div>
                {u.status && <div className="text-[11px] text-white/60 uppercase tracking-wider">{L(lang, u.status)}</div>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedUnitId(u.id); setUnitOpenSource("locate"); setMode("unit"); setLocateCard(null); trackEvent("unit_detail_open", { entity_id: u.id, source: "locate" }); }}
                  className="px-3 py-1.5 rounded-md bg-white text-zinc-900 text-xs uppercase tracking-wider font-medium hover:bg-white/90"
                  data-testid="locate-mini-details"
                >{L(lang, "details")}</button>
                <button
                  onClick={() => { setLocateCard(null); setHighlight((h) => (h?.kind === "unit" ? null : h)); }}
                  className="text-white/60 hover:text-white text-xs px-2"
                  data-testid="locate-mini-dismiss"
                  aria-label={L(lang, "dismiss")}
                >✕</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* iOS autoplay-blocked fallback. Tapping anywhere triggers the
          global unlock listener; this overlay just makes that moment
          look intentional instead of showing iOS's grey native button. */}
      {needsGesture && (        <div
          className="tap-to-begin fade-in"
          onClick={dismissGesture}
          data-testid="viewer-tap-to-begin"
          role="button"
          aria-label={L(lang, "tap_to_begin")}
        >
          <div className="flex flex-col items-center gap-5 text-center px-8">
            <div className="text-[10px] uppercase tracking-[0.4em] text-white/55">{projectName}</div>
            <div className="tap-to-begin-ring">
              <div className="tap-to-begin-arrow" />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/75">{L(lang, "tap_to_begin")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
